import React, { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Button, Card, CardBody, Col, Container, Input, Label, Nav, NavItem, NavLink, Row } from "reactstrap"
import Select from "react-select"
import CreatableSelect from "react-select/creatable"
import Breadcrumbs from "components/Common/Breadcrumb"
import { MetaTags } from "react-meta-tags"
import DateRangePicker, { DATE_RANGE_OPTIONS, createOption } from "components/Common/DateRangePicker"
import { isGoalBrand } from "config"
import { REPORTING_TIMEZONE, REQUIRED_FEATURE_FLAGS } from "helpers/sf_constants"
import { bsh_getAdminUserDetails, bsh_getClientUserDetails } from "helpers/sf_browser_storage_helper"
import useFeatureFlags from "hooks/useFeatureFlags"
import useFeatureFlagsForAccounts from "hooks/useFeatureFlagsForAccounts"
import {
  getAdGroupsByCampaign,
  getAdminManageClientAccountWithBrands,
  getAdminManageClientMarketingGroups,
  getAdminManageClientSourceTypes,
  getCampaignsByAccount,
} from "store/accounts/actions"
import {
  createRunEntry,
  createReportingErrorResult,
  buildReportingConsistencyScope,
  dateRangeToReportingDates,
  filtersFromRows,
  getFilterValueOptions,
  getSupportedFilterOptions,
  getSupportedGroupByOptions,
  parseFilterInput,
  REPORTING_COMPARISON_MODES,
  REPORTING_COMPARISON_OPTIONS,
  REPORTING_CONSISTENCY_FILTER_OPTIONS,
  REPORTING_PRODUCT_OPTIONS,
  getReportingProductOptions,
  runReportingApis,
} from "./reportingConsistencyHelper"
import { ReportingChecksTable, ReportingRunDetails } from "./ReportingConsistencyTables"
import ExportValidationTab from "./ExportValidationTab"

const ReportingConsistency = () => {
  const currentClient = bsh_getClientUserDetails()
  const dispatch = useDispatch()
  const [isFeatureFlagActive, areFeatureFlagsLoaded] = useFeatureFlags()
  const [isFeatureFlagActiveForAccount] = useFeatureFlagsForAccounts()
  const {
    currentAccount,
    accountDirectory,
    accountDirectoryLoading,
    campaignsByAccount,
    campaignsByAccountLoading,
    adGroupsByCampaign,
    adGroupsByCampaignLoading,
    sourceTypes,
    sourceTypesLoading,
    marketingGroups,
    marketingGroupsLoading,
  } = useSelector(state => ({
    currentAccount: state.accounts.current_account,
    accountDirectory: state.accounts.admin_manage_client_account_with_brands,
    accountDirectoryLoading: state.accounts.admin_manage_client_account_with_brands_loading,
    campaignsByAccount: state.accounts.admin_manage_client_campaigns_by_account,
    campaignsByAccountLoading: state.accounts.admin_manage_client_campaigns_by_account_loading,
    adGroupsByCampaign: state.accounts.admin_manage_client_ad_groups_by_campaign,
    adGroupsByCampaignLoading: state.accounts.admin_manage_client_ad_groups_by_campaign_loading,
    sourceTypes: state.accounts.admin_manage_client_source_types,
    sourceTypesLoading: state.accounts.admin_manage_client_source_types_loading,
    marketingGroups: state.accounts.admin_manage_client_marketing_groups,
    marketingGroupsLoading: state.accounts.admin_manage_client_marketing_groups_loading,
  }))
  const activeClientAccountId = currentClient?.account_id ? String(currentClient.account_id) : ""
  const hasClientSession = Boolean(currentClient?.token)
  const hasAdminSession = Boolean(bsh_getAdminUserDetails()?.token)
  const initialAccountId = activeClientAccountId
  const [accountId, setAccountId] = useState(initialAccountId)
  useEffect(() => {
    if (hasAdminSession && !accountDirectory?.length && !accountDirectoryLoading) {
      dispatch(getAdminManageClientAccountWithBrands())
    }
  }, [accountDirectory, accountDirectoryLoading, dispatch, hasAdminSession])

  const selectedAccount = useMemo(() => {
    if (currentAccount?.id && String(currentAccount.id) === String(accountId)) {
      return currentAccount
    }

    const directoryAccount = accountDirectory.find(account => String(account?.id) === String(accountId))
    if (directoryAccount) return directoryAccount

    if (currentClient?.account_id && String(currentClient.account_id) === String(accountId)) {
      return {
        id: Number(currentClient.account_id),
        ...(currentClient.internal === true ? { internal: true } : {}),
      }
    }

    return null
  }, [accountDirectory, accountId, currentAccount, currentClient])
  const dateRangeOptions = useMemo(() => Object.values(DATE_RANGE_OPTIONS)
    .filter(option => option !== DATE_RANGE_OPTIONS.ALL_TIME)
    .map(option => createOption(option, new Date(), REPORTING_TIMEZONE)), [])
  const [selectedDateRange, setSelectedDateRange] = useState(() => createOption(
    DATE_RANGE_OPTIONS.YESTERDAY,
    new Date(),
    REPORTING_TIMEZONE,
  ))
  const isLeadMarketplaceEnabled = isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_GOAL_MARKETPLACE)
  const goalReportingProductOptions = useMemo(() => getReportingProductOptions({
    isGoalBrand,
    isLeadMarketplaceEnabled,
  }), [isGoalBrand, isLeadMarketplaceEnabled])
  const reportingProductOptions = isGoalBrand ? goalReportingProductOptions : REPORTING_PRODUCT_OPTIONS
  const [selectedProduct, setSelectedProduct] = useState(reportingProductOptions[0])
  const [selectedComparisonMode, setSelectedComparisonMode] = useState(REPORTING_COMPARISON_OPTIONS[0])
  const [selectedGroupBy, setSelectedGroupBy] = useState([{ label: "Campaigns", value: "campaign_id" }])
  const [filterRows, setFilterRows] = useState([])
  useEffect(() => {
    if (!reportingProductOptions.some(option => option.value === selectedProduct?.value)) {
      setSelectedProduct(reportingProductOptions[0])
    }
  }, [reportingProductOptions, selectedProduct?.value])
  const isAdminPortalMode = selectedComparisonMode.value === REPORTING_COMPARISON_MODES.ADMIN_PORTAL
  const groupByOptions = useMemo(() => getSupportedGroupByOptions({
    productTypeId: selectedProduct?.value,
    isFeatureFlagActive,
  }), [isFeatureFlagActive, selectedProduct?.value])
  const selectedFilters = useMemo(() => filtersFromRows(filterRows), [filterRows])
  const selectedAccountNumber = Number(accountId)
  const supportedFilterOptions = useMemo(() => getSupportedFilterOptions({
    productTypeId: selectedProduct?.value,
    isFeatureFlagActive,
  }), [isFeatureFlagActive, selectedProduct?.value])
  const accountCampaignIds = useMemo(() => campaignsByAccount
    .filter(campaign => Number(campaign?.account_id) === selectedAccountNumber)
    .map(campaign => Number(campaign.id))
    .filter(id => Number.isInteger(id) && id > 0), [campaignsByAccount, selectedAccountNumber])
  useEffect(() => {
    if (!isAdminPortalMode || !hasAdminSession || !Number.isInteger(selectedAccountNumber) || selectedAccountNumber < 1) return
    dispatch(getCampaignsByAccount({ account_ids: [selectedAccountNumber], brand_ids: null }))
    dispatch(getAdminManageClientSourceTypes())
    if (isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_MARKETING_GROUP_ANALYTICS)) {
      dispatch(getAdminManageClientMarketingGroups())
    }
  }, [dispatch, hasAdminSession, isAdminPortalMode, isFeatureFlagActive, selectedAccountNumber])
  useEffect(() => {
    if (!isAdminPortalMode || !hasAdminSession || !accountCampaignIds.length) return
    dispatch(getAdGroupsByCampaign({ campaign_ids: accountCampaignIds }))
  }, [accountCampaignIds, dispatch, hasAdminSession, isAdminPortalMode])
  const filterValueOptions = useMemo(() => Object.fromEntries(
    supportedFilterOptions.map(({ key }) => [key, getFilterValueOptions({
      key,
      account: selectedAccount,
      campaigns: campaignsByAccount,
      adGroups: adGroupsByCampaign,
      sourceTypes,
      marketingGroups,
      productTypeId: selectedProduct?.value,
    })]),
  ), [adGroupsByCampaign, campaignsByAccount, marketingGroups, selectedAccount, selectedProduct?.value, sourceTypes, supportedFilterOptions])
  const filterValueLoading = {
    campaign_ids: campaignsByAccountLoading,
    ad_group_ids: adGroupsByCampaignLoading,
    source_type_ids: sourceTypesLoading,
    marketing_groups: marketingGroupsLoading,
  }
  const filterKeyOptions = useMemo(() => supportedFilterOptions
    .map(({ key, label }) => ({ label, value: key })), [supportedFilterOptions])
  useEffect(() => {
    setFilterRows(current => {
      const next = current.map(row => filterKeyOptions.some(option => option.value === row.key)
        ? row
        : { ...row, key: null, values: [] })
      return next.every((row, index) => row === current[index]) ? current : next
    })
  }, [filterKeyOptions])
  useEffect(() => {
    setSelectedGroupBy(current => {
      const next = current.filter(group => groupByOptions.some(option => option.value === group.value))
      return next.length === current.length ? current : next
    })
  }, [groupByOptions])
  const selectedDates = dateRangeToReportingDates(selectedDateRange)
  const [runs, setRuns] = useState(() => {
    return []
  })
  const [expandedRunId, setExpandedRunId] = useState(null)
  const [running, setRunning] = useState(false)
  const [error, setError] = useState("")
  const [activeTab, setActiveTab] = useState("surfaces")

  const addRun = response => {
    const runEntry = createRunEntry(response)
    setRuns(previous => [runEntry, ...previous])
    setExpandedRunId(runEntry.id)
  }

  const run = async () => {
    setRunning(true)
    setError("")
    try {
      const response = await runReportingApis(Number(accountId), selectedDates, selectedProduct.value, {
        isFeatureFlagActive,
        account: selectedAccount,
        isGoalBrand,
        isFeatureFlagActiveForAccount,
        comparisonMode: selectedComparisonMode.value,
        groupByParams: isAdminPortalMode ? selectedGroupBy.map(group => group.value) : ["campaign_id"],
        filters: isAdminPortalMode ? selectedFilters : {},
      })
      addRun(response)
    } catch (runError) {
      addRun(createReportingErrorResult({
        accountId,
        dates: selectedDates,
        error: runError,
        productTypeId: selectedProduct.value,
        isGoalBrand,
        scope: buildReportingConsistencyScope({
          accountId,
          selectedDates,
          productTypeId: selectedProduct.value,
          isGoalBrand,
          comparisonMode: selectedComparisonMode.value,
          groupByParams: isAdminPortalMode ? selectedGroupBy.map(group => group.value) : ["campaign_id"],
          filters: isAdminPortalMode ? selectedFilters : {},
        }),
      }))
    } finally {
      setRunning(false)
    }
  }

  const addFilterRow = () => {
    setFilterRows(current => [...current, { id: `${Date.now()}-${current.length}`, key: null, values: [] }])
  }

  const updateFilterRow = (id, changes) => {
    setFilterRows(current => current.map(row => row.id === id ? { ...row, ...changes } : row))
  }

  const removeFilterRow = id => {
    setFilterRows(current => current.filter(row => row.id !== id))
  }

  const expandedRun = runs.find(runEntry => runEntry.id === expandedRunId)

  return (
    <React.Fragment>
      <MetaTags><title>Reporting Consistency</title></MetaTags>
      <div className="page-content reporting-consistency">
        <Container fluid>
          <Breadcrumbs title="Reporting" breadcrumbItem="Reporting Consistency" />
          <Row><Col lg="12">
            <Card className="shadow-sm border-0"><CardBody className="p-4">
              <div className="d-flex justify-content-between align-items-start mb-2">
                <div>
                  <h4 className="card-title mb-1">Reporting consistency</h4>
                  <p className="text-muted mb-0">Compare the reporting surfaces for one client account using the current admin session.</p>
                </div>
                <span className="text-muted reporting-consistency__period">{selectedProduct.label} · {selectedDateRange.label} · PST</span>
              </div>
              <div className="bg-light rounded p-3 mt-4">
                <Row className="align-items-end">
                  <Col md="3"><Label className="font-weight-bold d-block">Account ID</Label><Input type="number" min="1" placeholder="Enter account ID" value={accountId} onChange={event => setAccountId(event.target.value)} /></Col>
                  <Col md="3"><Label className="font-weight-bold d-block">Product</Label><Select
                    value={selectedProduct}
                    options={reportingProductOptions}
                    onChange={option => option && setSelectedProduct(option)}
                    isSearchable={false}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  /></Col>
                  <Col md="3"><Label className="font-weight-bold d-block">Timeframe</Label><DateRangePicker
                    ianaTz={REPORTING_TIMEZONE}
                    value={selectedDateRange}
                    options={dateRangeOptions}
                    onChange={setSelectedDateRange}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  /></Col>
                  <Col md="3"><Label className="font-weight-bold d-block">Comparison</Label><Select
                    value={selectedComparisonMode}
                    options={REPORTING_COMPARISON_OPTIONS}
                    onChange={option => option && setSelectedComparisonMode(option)}
                    isSearchable={false}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  /></Col>
                </Row>
                {isAdminPortalMode && <>
                  <Row className="mt-3">
                    <Col md="12">
                      <Label className="font-weight-bold d-block">Group by</Label>
                      <Select
                        value={selectedGroupBy}
                        options={groupByOptions}
                        isMulti
                        closeMenuOnSelect={false}
                        onChange={options => setSelectedGroupBy(options || [])}
                        isSearchable
                        placeholder="Select shared groupings"
                        classNamePrefix="reporting-consistency-select"
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      <small className="form-text text-muted">Only groupings exposed by both Admin Analytics and Portal Analytics are available.</small>
                    </Col>
                  </Row>
                  <div className="mt-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <Label className="font-weight-bold mb-0">Filters</Label>
                      <Button type="button" color="link" size="sm" className="p-0" onClick={addFilterRow}>Add filter</Button>
                    </div>
                    {!filterRows.length && <small className="form-text text-muted">No filters selected. Add a filter to choose a key and one or more values.</small>}
                    {filterRows.map(row => {
                      const selectedKey = filterKeyOptions.find(option => option.value === row.key)
                      const selectedKeys = new Set(filterRows.filter(other => other.id !== row.id).map(other => other.key).filter(Boolean))
                      const filterConfig = REPORTING_CONSISTENCY_FILTER_OPTIONS.find(option => option.key === row.key)
                      const ValueSelect = filterConfig?.numeric ? CreatableSelect : Select
                      return (
                        <Row key={row.id} className="reporting-consistency__filter-row mb-2 align-items-start">
                          <Col md="4" className="d-flex">
                            <Select
                              value={selectedKey || null}
                              options={filterKeyOptions.filter(option => option.value === row.key || !selectedKeys.has(option.value))}
                              placeholder="Filter key"
                              classNamePrefix="reporting-consistency-select"
                              onChange={option => updateFilterRow(row.id, { key: option?.value || null, values: [] })}
                              isClearable
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                            />
                          </Col>
                          <Col md="7" className="d-flex flex-column">
                            <ValueSelect
                              isMulti
                              value={row.values}
                              options={filterValueOptions[row.key] || []}
                              isDisabled={!row.key}
                              isLoading={Boolean(filterValueLoading[row.key])}
                              placeholder={row.key ? "Select filter value(s)" : "Select a filter key first"}
                              noOptionsMessage={() => filterConfig?.numeric
                                ? "No values available; type an ID to add it"
                                : "No values available"}
                              isValidNewOption={inputValue => filterConfig?.numeric && parseFilterInput(inputValue, true).length === 1}
                              formatCreateLabel={inputValue => `Use ID "${inputValue}"`}
                              classNamePrefix="reporting-consistency-select"
                              onChange={values => updateFilterRow(row.id, { values: values || [] })}
                              onCreateOption={inputValue => {
                                const createdValues = parseFilterInput(inputValue, filterConfig?.numeric)
                                  .map(value => ({ label: String(value), value }))
                                const existingValues = new Set(row.values.map(value => String(value.value)))
                                updateFilterRow(row.id, {
                                  values: [
                                    ...row.values,
                                    ...createdValues.filter(value => !existingValues.has(String(value.value))),
                                  ],
                                })
                              }}
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                            />
                            {filterConfig?.numeric && <small className="form-text text-muted">Select one or more IDs.</small>}
                          </Col>
                          <Col md="1" className="d-flex justify-content-center"><Button type="button" color="link" className="text-danger" aria-label="Remove filter" onClick={() => removeFilterRow(row.id)}><i className="bx bx-trash" /></Button></Col>
                        </Row>
                      )
                    })}
                  </div>
                </>}
                <Row className="mt-3">
                  <Col md="10"><small className="form-text text-muted">{isAdminPortalMode ? "The Admin Analytics and Portal Analytics checks use the same selected date, grouping, and filters." : "This preserves the existing all-surfaces check across Admin Analytics, Portal Analytics, Campaign Stats, Dashboard, and campaign child stats."}</small></Col>
                  <Col md="2" className="d-flex align-items-end"><Button color="primary" className="w-100" disabled={running || !accountId || !hasAdminSession || !areFeatureFlagsLoaded || !selectedAccount} onClick={run}>{running ? "Running checks..." : "Run consistency check"}</Button></Col>
                </Row>
                <small className="form-text text-muted mt-2">The account and reporting timeframe to include in this check.</small>
                {!areFeatureFlagsLoaded && <small className="d-block text-muted mt-2">Loading account and reporting API feature flags...</small>}
                {accountId && !selectedAccount && accountDirectoryLoading && <small className="d-block text-muted mt-2">Loading the selected account configuration...</small>}
                <div className="mt-3">
                  {hasClientSession && activeClientAccountId
                    ? <span className="badge bg-success">Existing client session: account {activeClientAccountId}</span>
                    : <span className="badge bg-info">Admin session will open the selected account automatically</span>}
                  {!hasClientSession && <small className="d-block text-muted mt-2">No manual account opening is required. The current admin session is used for the account handoff.</small>}
                  {hasClientSession && activeClientAccountId && accountId && accountId !== activeClientAccountId && <small className="d-block text-muted mt-2">The current client token is for account {activeClientAccountId}; this run will use the admin session to open account {accountId} automatically.</small>}
                </div>
              </div>
              {error && <div className="alert alert-danger">{error}</div>}
            </CardBody></Card>
          </Col></Row>
          <Nav tabs className="mb-4">
            <NavItem><NavLink className={activeTab === "surfaces" ? "active" : ""} onClick={() => setActiveTab("surfaces")} href="#surfaces">Surface checks</NavLink></NavItem>
            <NavItem><NavLink className={activeTab === "exports" ? "active" : ""} onClick={() => setActiveTab("exports")} href="#exports">Export validation</NavLink></NavItem>
          </Nav>
          {activeTab === "surfaces" && <Row><Col lg="12"><Card className="shadow-sm border-0"><CardBody className="p-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div><h4 className="card-title mb-1">Checks this session</h4><p className="text-muted mb-0">Click a check to see which reporting surfaces matched.</p></div>
              <span className="text-muted reporting-consistency__period">{runs.length} check{runs.length === 1 ? "" : "s"}</span>
            </div>
            <ReportingChecksTable runs={runs} expandedRunId={expandedRunId} onRunClick={runId => setExpandedRunId(current => current === runId ? null : runId)} />
            {expandedRun && <ReportingRunDetails result={expandedRun.result} />}
          </CardBody></Card></Col></Row>}
          {activeTab === "exports" && <Row><Col lg="12"><ExportValidationTab selectedDateRange={selectedDateRange} dateRangeOptions={dateRangeOptions} onDateRangeChange={setSelectedDateRange} accountId={accountId} onAccountIdChange={setAccountId} /></Col></Row>}
        </Container>
      </div>
    </React.Fragment>
  )
}

export default ReportingConsistency
